import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useStores } from "../../../hooks/useStores";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import {
  Link as CLink,
  Box,
  Input,
  FormLabel,
  HStack,
  Button,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { VirtualizedTable } from "../../../components/VirtualizedTable";
import moment from "moment";
import { Container } from "../styled";
import { User } from "../../../types";
import useDebounce from "../../../hooks/useDebounce";
import styled from "styled-components";
import api from "../../../services/api";
import dayjs from "dayjs";
import { getSubscriptionStatus } from "../../../utils/subscription";

export const TabFewWorkouts: React.FC = observer(() => {
  const { clientsStore } = useStores();
  const { t } = useTranslation();

  const [searchQuery, setSearchQuery] = useState<string>("30");
  const debouncedQuery = useDebounce(searchQuery, 1000);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<
    "ASC" | "DESC" | undefined
  >(undefined);

  useEffect(() => {
    clientsStore.getClientsFewWorkouts(parseInt(debouncedQuery) || 0);
  }, [debouncedQuery]);

  const columns = useMemo(
    () => [
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.client.customerTableHead.surname"),
        dataKey: "surname",
        sorter: (a: { surname: string }, b: { surname: any }) =>
          a.surname.localeCompare(b.surname),
        render: (surname: string, user: User) => {
          return (
            <CLink as={Link} to={`/admin/clients/${user.id}`} color="purple">
              {surname}
            </CLink>
          );
        },
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.client.customerTableHead.name"),
        dataKey: "name",
        sorter: (a: { name: string }, b: { name: any }) =>
          a.name.localeCompare(b.name),
        render: (name: string, user: User) => {
          return (
            <CLink as={Link} to={`/admin/clients/${user.id}`} color="purple">
              {name}
            </CLink>
          );
        },
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.client.customerTableHead.email"),
        dataKey: "email",
        sorter: (a: { email: string }, b: { email: any }) =>
          a.email.localeCompare(b.email),
        render: (email: string) => {
          return email;
        },
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.client.customerTableHead.registrationDate"),
        dataKey: "created_at",
        sorter: (
          a: { createdAt: moment.MomentInput },
          b: { createdAt: moment.MomentInput }
        ) => moment.utc(a.createdAt).diff(moment.utc(b.createdAt)),
        render: (createdAt: string) => {
          return moment(createdAt).format("DD/MM/YYYY");
        },
      },
      {
        width: 80,
        flexGrow: 1,
        label: "Numero attività",
        dataKey: "activities_count",
        sorter: (
          a: { activities_count: number; reservations_count: number },
          b: { activities_count: number; reservations_count: number }
        ) =>
          a.activities_count +
          a.reservations_count -
          (b.activities_count + b.reservations_count),
        render: (activitiesCount: number, row: any) =>
          activitiesCount + row.reservations_count,
      },
      {
        width: 80,
        flexGrow: 1,
        label: "Stato abbonamento",
        dataKey: "subscription_status",
        cellDataGetter: (dataKey: any, rowData: any) => {
          return getSubscriptionStatus(dataKey.rowData as User);
        },
        render: (subscription_status: User) => {
          return subscription_status;
        },
      },
    ],
    [t]
  );

  const activities = useMemo(() => {
    let clientsFewWorkouts = [...clientsStore.clientsFewWorkouts];
    if (sortBy) {
      const columnData = columns.find((column) => column.dataKey === sortBy);

      if (columnData?.sorter) {
        // @ts-ignore
        clientsFewWorkouts = clientsFewWorkouts.sort(columnData.sorter);

        if (sortDirection === "DESC") {
          clientsFewWorkouts = clientsFewWorkouts.reverse();
        }
      }
    }

    return clientsFewWorkouts;
  }, [clientsStore.clientsFewWorkouts, sortBy, columns, sortDirection]);

  const handleSort = useCallback(
    (dataKey: string) => {
      if (dataKey === sortBy && sortDirection === "ASC") {
        setSortDirection("DESC");
      } else if (dataKey === sortBy && sortDirection === "DESC") {
        setSortBy(undefined);
        setSortDirection(undefined);
      } else {
        setSortBy(dataKey);
        setSortDirection("ASC");
      }
    },
    [sortBy, sortDirection, setSortBy, setSortDirection]
  );

  const handleDownloadReport = useCallback(async () => {
    const rows = [
      [
        t("screens.client.customerTableHead.surname").toUpperCase(),
        t("screens.client.customerTableHead.name").toUpperCase(),
        t("screens.client.customerTableHead.email").toUpperCase(),
        t("screens.client.customerTableHead.registrationDate").toUpperCase(),
        "NUMERO ALLENAMENTI",
        "STATO ABBONAMENTO",
      ],
    ];

    for (let customer of clientsStore.clientsFewWorkouts) {
      rows.push([
        customer.surname,
        customer.name,
        customer.email,
        // @ts-ignore
        moment(customer.created_at).format("DD/MM/YYYY"),
        // @ts-ignore
        customer.activities_count,
        getSubscriptionStatus(customer),
      ]);
    }

    const blob = await api.generateExportFile(rows);

    if (blob) {
      //  Create blob link to download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${dayjs().format("DD-MM-YYYY")}_export_attività_svolte.xlsx`
      );

      //  Append to html page
      document.body.appendChild(link);
      // Force download
      link.click();
      // Clean up and remove the link
      link.parentNode?.removeChild(link);
    }
  }, [clientsStore.clientsFewWorkouts, t]);

  return (
    <Container>
      <HStack justifyContent="flex-end" w="100%">
        <Button
          colorScheme={"purple"}
          variant={"ghost"}
          onClick={handleDownloadReport}
        >
          Scarica report
        </Button>
      </HStack>

      <Box display="flex" w="100%">
        <StyledFormLabel>
          {t("screens.reports.tabSubscriptionCancellation.daysNumber")}
        </StyledFormLabel>

        <Input
          placeholder={t(
            "screens.reports.tabSubscriptionCancellation.daysNumberPlaceholder"
          )}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
        />
      </Box>

      <div
        style={{
          flex: "1 1 auto",
          width: "100%",
          height: `${(activities.length + 1) * 50}px`,
          display: "flex",
        }}
      >
        <VirtualizedTable
          rowCount={activities.length}
          rowGetter={({ index }) => activities[index]}
          columns={columns}
          rowStyle={(row) =>
            row.index % 2 === 0 ? { background: "#EDF1FB" } : {}
          }
          onHeaderCellClick={handleSort}
          sortBy={sortBy}
          sortDirection={sortDirection}
        />
      </div>
    </Container>
  );
});

const StyledFormLabel = styled(FormLabel)`
  color: ${(props) => props.theme.colors.secondaryText1};
  font-family: Helvetica Neue;
  font-style: normal;
  font-weight: normal !important;
  font-size: 17px !important;
  line-height: 12px;
  flex: none;
  align-self: center;
`;
